import { isEmpty, keys, partition, sum, values } from "lodash";
import React, { useMemo } from "react";
import { useWallets } from "../../../context/wallets";
import { BONES_PER_HNT, useHntMarket } from "../../../utils/useHntMarket";

const WalletsSection: React.FC = () => {
  const { wallets, toggleAddWalletSidebar } = useWallets();
  const { displayInCurrency } = useHntMarket();

  const [trackedWallets, untrackedWallets] = useMemo(
    () => partition(values(wallets), (wallet) => wallet.tracked),
    [wallets]
  );

  const onlyTrackedOrUntrackedWallets = useMemo(
    () => isEmpty(trackedWallets) || isEmpty(untrackedWallets),
    [trackedWallets, untrackedWallets]
  );

  const balance: {
    [key: string]: { title: string; amount: string; hidden: boolean }[];
  } = useMemo(
    () => ({
      hnt: [
        {
          title: `${values(wallets).length > 1 ? "Total " : ""}HNT`,
          amount: displayInCurrency(
            sum(
              values(wallets).map((wallet) => wallet.balance_data.balance || 0)
            ),
            { needsDivision: true }
          ),
          hidden: false
        },
        {
          title: `Tracked Wallet${trackedWallets.length > 1 ? "s" : ""} (HNT)`,
          amount: displayInCurrency(
            sum(
              trackedWallets.map((wallet) => wallet.balance_data.balance || 0)
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        },
        {
          title: ` Untracked Wallet${untrackedWallets.length > 1 ? "s" : ""}
          (HNT)`,
          amount: displayInCurrency(
            sum(
              untrackedWallets.map((wallet) => wallet.balance_data.balance || 0)
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        }
      ],
      mobile: [
        {
          title: `${values(wallets).length > 1 ? "Total " : ""}MOBILE`,
          amount: formatMobileBalance(
            sum(
              values(wallets).map(
                (wallet) => wallet.balance_data.mobile_balance || 0
              )
            )
          ),
          hidden: false
        },
        {
          title: `Tracked Wallet${
            trackedWallets.length > 1 ? "s" : ""
          } (MOBILE Balance)`,
          amount: formatMobileBalance(
            sum(
              trackedWallets.map(
                (wallet) => wallet.balance_data.mobile_balance || 0
              )
            )
          ),
          hidden: onlyTrackedOrUntrackedWallets
        },
        {
          title: ` Untracked Wallet${untrackedWallets.length > 1 ? "s" : ""}
          (MOBILE Balance)`,
          amount: formatMobileBalance(
            sum(
              untrackedWallets.map(
                (wallet) => wallet.balance_data.mobile_balance || 0
              )
            )
          ),
          hidden: onlyTrackedOrUntrackedWallets
        }
      ],
      iot: [
        {
          title: `${values(wallets).length > 1 ? "Total " : ""}IOT`,
          amount: displayInCurrency(
            sum(
              values(wallets).map(
                (wallet) => wallet.balance_data.iot_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: false
        },
        {
          title: `Tracked Wallet${trackedWallets.length > 1 ? "s" : ""} (IOT)`,
          amount: displayInCurrency(
            sum(
              trackedWallets.map(
                (wallet) => wallet.balance_data.iot_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        },
        {
          title: ` Untracked Wallet${untrackedWallets.length > 1 ? "s" : ""}
          (IOT)`,
          amount: displayInCurrency(
            sum(
              untrackedWallets.map(
                (wallet) => wallet.balance_data.iot_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        }
      ],
      dc: [
        {
          title: `${values(wallets).length > 1 ? "Total " : ""}DC`,
          amount: displayInCurrency(
            sum(
              values(wallets).map(
                (wallet) => wallet.balance_data.dc_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: false
        },
        {
          title: `Tracked Wallet${trackedWallets.length > 1 ? "s" : ""} (DC)`,
          amount: displayInCurrency(
            sum(
              trackedWallets.map(
                (wallet) => wallet.balance_data.dc_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        },
        {
          title: ` Untracked Wallet${untrackedWallets.length > 1 ? "s" : ""}
          (DC)`,
          amount: displayInCurrency(
            sum(
              untrackedWallets.map(
                (wallet) => wallet.balance_data.dc_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        }
      ],
      hnt_staked: [
        {
          title: `${values(wallets).length > 1 ? "Total " : ""}HNT Staked`,
          amount: displayInCurrency(
            sum(
              values(wallets).map(
                (wallet) => wallet.balance_data.staked_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: false
        },
        {
          title: `Tracked Wallet${
            trackedWallets.length > 1 ? "s" : ""
          } (HNT Staked)`,
          amount: displayInCurrency(
            sum(
              trackedWallets.map(
                (wallet) => wallet.balance_data.staked_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        },
        {
          title: ` Untracked Wallet${untrackedWallets.length > 1 ? "s" : ""}
          (HNT Staked)`,
          amount: displayInCurrency(
            sum(
              untrackedWallets.map(
                (wallet) => wallet.balance_data.staked_balance || 0
              )
            ),
            { needsDivision: true }
          ),
          hidden: onlyTrackedOrUntrackedWallets
        }
      ]
    }),
    [
      displayInCurrency,
      onlyTrackedOrUntrackedWallets,
      trackedWallets,
      untrackedWallets,
      wallets
    ]
  );

  return (
    <section className="mt-6">
      <div className="pb-5 border-b border-gray-200 dark:border-gray-500">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          <span className="flex-1">Wallets</span>
          <button
            type="button"
            className="ml-4 inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
            onClick={() => toggleAddWalletSidebar()}
          >
            Add wallet
          </button>
        </h3>
      </div>

      <div className="mt-6">
        {isEmpty(wallets) ? (
          <div className="dark:text-gray-300">
            Add wallets to your workspace to track their balances and hotspots.
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {keys(balance).map((currency) => (
              <>
                {balance[currency]
                  .filter(
                    (item) =>
                      !item.hidden && balance[currency][0].amount !== "0.00"
                  )
                  .map((item) => (
                    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
                      <div className="px-4 py-5 sm:p-6">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate">
                          {item.title}
                        </dt>
                        <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                          {item.amount}
                        </dd>
                      </div>
                    </div>
                  ))}
              </>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
};

export default WalletsSection;

//
// Utils
//

export const formatMobileBalance = (balance: number) =>
  (balance / BONES_PER_HNT).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
